import EventEmitter from 'events';
class Call {
    constructor(userAgent, direction, target, options) {
        // Create a new EventEmitter
        const emitter = new EventEmitter();
        // 25 listeners is all we need for now
        // This can be increased as needs be
        emitter.setMaxListeners(25);
        // Event handlers object to be passed to JsSIP
        const eventHandlers = {};
        // All possible events which can be emitted
        const events = [
            'peerconnection',
            'connecting',
            'sending',
            'progress',
            'accepted',
            'confirmed',
            'ended',
            'failed',
            'newDTMF',
            'hold',
            'unhold',
            'muted',
            'unmuted',
            'reinvite',
            'update',
            'refer',
            'refersucceeded',
            'referfailed',
            'replaces',
        ];
        // Make UA available for later use
        this.userAgent = userAgent;
        // We'll populate this later
        this.streams = {};
        this.tags = [];
        this.options = Object.assign({}, options);
        this.on = () => emitter;
        this.once = () => emitter;
        if (direction === 'OUT') {
            this.target = target;
            // Convenience function to alias emitter.on() and emitter.once()
            this.on = (e, listener) => emitter.on(e, listener);
            this.once = (e, listener) => emitter.once(e, listener);
            this.options.eventHandlers = eventHandlers;
        }
        else if (direction === 'IN') {
            const session = target;
            // Convenience function to alias session.on() and session.once()
            this.on = (e, listener) => session.on(e, listener);
            this.once = (e, listener) => session.once(e, listener);
            this.session = session;
        }
        // For each event, bind an emitter to that event's handler and
        // set up a listener for that event to call a local function of
        // the same name (prefixed with '_', suffixed with 'EventHandler')
        events.forEach((e) => {
            eventHandlers[e] = (passedEvent) => {
                emitter.emit(e, passedEvent);
            };
            const eventHandlerName = `_${e}EventHandler`;
            const eventHandler = this[eventHandlerName];
            if (typeof eventHandler === 'function') {
                this.on(e, (passedEvent) => {
                    // We have to pass a reference to 'this' in
                    // to the event handler otherwise it may
                    // not be able to access the call
                    eventHandler(this, passedEvent);
                });
            }
            else {
                this.on(e, (passedEvent) => {
                    console.debug(`emitter ${e} event:`, passedEvent);
                });
            }
        });
        this.emitter = emitter;
        this.isOnHold = false;
        this.isMuted = false;
        this.answered = false;
        this.direction = direction;
    }
    _buildSipURI(target) {
        return `sip:${target}@${this.userAgent.domain}`;
    }
    invite() {
        // Initiates the call
        this.session = this.userAgent.call(this._buildSipURI(this.target), this.options);
    }
    connect() {
        this.session.answer(this.options);
    }
    refer(number, options) {
        // Transfers the call
        const target = number ? this._buildSipURI(number) : '';
        const referer = this.session.refer(target, options);
        const { emitter } = this;
        referer.on('requestSucceeded', (e) => {
            if (this.direction === 'IN') {
                this.session.emit('refersucceeded', e);
            }
            else {
                emitter.emit('refersucceeded', e);
            }
            this.userAgent.removeOngoingCall(this.session.id);
        });
        referer.on('requestFailed', (e) => {
            if (this.direction === 'IN') {
                this.session.emit('referfailed', e);
            }
            else {
                emitter.emit('referfailed', e);
            }
        });
        return referer;
    }
    sendDTMF(tone) {
        this.session.sendDTMF(tone);
    }
    _peerconnectionEventHandler(call) {
        if (call.direction === 'IN') {
            call.session.connection.addEventListener('track', (event) => {
                call.streams.remote = event.streams[0];
                call._bindRemoteAudioElement(event.streams[0]);
            });
        }
    }
    _acceptedEventHandler(call) {
        call.startTime = new Date();
        call.answered = true;
    }
    _connectingEventHandler(call) {
        if (call.direction === 'OUT') {
            call.session.connection.addEventListener('track', (event) => {
                call.streams.remote = event.streams[0];
                call._bindRemoteAudioElement(event.streams[0]);
            });
        }
        call.userAgent.addOngoingCall(call);
    }
    bye() {
        this.session.terminate();
    }
    _endedEventHandler(call) {
        call.endTime = new Date();
        call.userAgent.removeOngoingCall(call.session.id);
    }
    _failedEventHandler(call) {
        call.userAgent.removeOngoingCall(call.session.id);
    }
    hold() {
        this.session.hold();
    }
    _holdEventHandler(call) {
        call.isOnHold = true;
        call._unbindLocalAudioElements();
        call._unbindRemoteAudioElements();
    }
    unhold() {
        const activeCall = this.userAgent.getActiveCall();
        const call = this.session;
        if (activeCall) {
            if (activeCall.answered) {
                activeCall.once('hold', () => {
                    call.unhold();
                });
                activeCall.hold();
            }
            else {
                call.unhold();
            }
        }
        else {
            call.unhold();
        }
    }
    _unholdEventHandler(call) {
        call.isOnHold = false;
        call._bindLocalAudioElement();
    }
    mute() {
        this.session.mute();
    }
    _mutedEventHandler(call) {
        call.isMuted = true;
    }
    unmute() {
        this.session.unmute();
    }
    _unmutedEventHandler(call) {
        call.isMuted = false;
    }
    _confirmedEventHandler(call, e) {
        console.debug('emitter confirmed event: ', e);
        const stream = call.session.connection.getLocalStreams()[0];
        call.streams.local = stream;
        call._bindLocalAudioElement(stream);
    }
    _unbindLocalAudioElements() {
        const el = this.userAgent.audio.local;
        if (el) {
            el.src = '';
        }
    }
    _unbindRemoteAudioElements() {
        const el = this.userAgent.audio.remote;
        if (el) {
            el.src = '';
        }
    }
    _bindLocalAudioElement() {
        const el = this.userAgent.audio.local;
        const stream = this.streams.local;
        if (el) {
            el.srcObject = stream;
        }
    }
    _bindRemoteAudioElement() {
        const el = this.userAgent.audio.remote;
        const stream = this.streams.remote;
        if (el) {
            el.srcObject = stream;
        }
    }
}
export default Call;
